import { create } from 'zustand'
import { api, ApiError, type ApiUser } from '../lib/api'

interface AuthState {
  user: ApiUser | null
  /** 'unknown' until the first /me check resolves */
  status: 'unknown' | 'out' | 'in'
  busy: boolean
  error: string | null
  init: () => Promise<void>
  login: (email: string, password: string) => Promise<boolean>
  register: (email: string, name: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  clearError: () => void
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'unknown',
  busy: false,
  error: null,

  init: async () => {
    try {
      const { user } = await api.me()
      set({ user, status: user ? 'in' : 'out' })
    } catch {
      set({ status: 'out' }) // offline — treat as signed out until reachable
    }
  },

  login: async (email, password) => {
    set({ busy: true, error: null })
    try {
      const { user } = await api.login(email, password)
      set({ user, status: 'in', busy: false })
      return true
    } catch (e) {
      set({ busy: false, error: e instanceof ApiError ? e.message : 'network error — try again' })
      return false
    }
  },

  register: async (email, name, password) => {
    set({ busy: true, error: null })
    try {
      const { user } = await api.register(email, name, password)
      set({ user, status: 'in', busy: false })
      return true
    } catch (e) {
      set({ busy: false, error: e instanceof ApiError ? e.message : 'network error — try again' })
      return false
    }
  },

  logout: async () => {
    try {
      await api.logout()
    } catch {
      // clearing local state is what matters
    }
    set({ user: null, status: 'out' })
  },

  clearError: () => set({ error: null }),
}))
